'use client'

import * as TogglePrimitive from '@radix-ui/react-toggle'
import * as ToolbarPrimitive from '@radix-ui/react-toolbar'
import { cva, VariantProps } from 'class-variance-authority'
import React, {
  ComponentPropsWithoutRef,
  FC,
  HTMLAttributes,
  ReactNode,
  Ref
} from 'react'

import { SimpleTooltip } from '../components/tooltip/simple-tooltip'
import { ArrowDown } from '../icons/arrow-down'
import { cn } from '../utils/cn'
import { Separator } from './separator'

type TogglePrimitiveProps = typeof TogglePrimitive.Root

const BASE_TOGGLE_BUTTON =
  'inline-flex items-center justify-center rounded-md text-sm transition-colors hover:bg-slate-200 hover:text-yd-blue focus:bg-slate-200 focus:text-yd-blue disabled:pointer-events-none disabled:opacity-50 aria-pressed:bg-yd-blue aria-pressed:text-white data-[state=on]:bg-yd-blue data-[state=on]:text-white'

// @TODO remove data-[state=open] when move from toggle to button
const toggleVariants = cva(BASE_TOGGLE_BUTTON, {
  variants: {
    variant: {
      default: 'bg-transparent',
      outline:
        'border border-slate-200 bg-transparent hover:bg-slate-100 hover:text-slate-900'
    },
    size: {
      default: 'h-10 px-4',
      sm: 'h-9 px-3',
      lg: 'h-12 px-6',
      icon: 'h-10 w-10'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
})

type ToggleProps = React.ComponentPropsWithoutRef<TogglePrimitiveProps> &
  VariantProps<typeof toggleVariants>

const toolbarVariants = cva('relative flex select-none flex-wrap gap-1')

interface ToolbarProps
  extends ComponentPropsWithoutRef<typeof ToolbarPrimitive.Root>,
    VariantProps<typeof toolbarVariants> {
  ref?: Ref<HTMLDivElement>
}

const Toolbar: FC<ToolbarProps> = ({ className, ref, ...props }) => (
  <ToolbarPrimitive.Root
    ref={ref}
    className={cn(toolbarVariants(), className)}
    {...props}
  />
)

interface ToolbarButtonProps
  extends ComponentPropsWithoutRef<typeof ToolbarPrimitive.Button>,
    VariantProps<typeof toggleVariants>,
    Omit<ToggleProps, 'type'> {
  buttonType?: 'button' | 'toggle'
  pressed?: boolean
  tooltip?: ReactNode
  isDropdown?: boolean
}

const ToolbarButton: FC<ToolbarButtonProps> = ({
  className,
  variant,
  size,
  isDropdown,
  children,
  pressed,
  tooltip,
  ...props
}) => {
  const content = (
    <ToolbarPrimitive.Button
      className={cn(
        toggleVariants({
          variant,
          size
        }),
        isDropdown && 'justify-between',
        'gap-x-2',
        className
      )}
      aria-pressed={pressed}
      {...props}>
      {children}
      {isDropdown ? <ArrowDown aria-hidden="true" size={18} /> : null}
    </ToolbarPrimitive.Button>
  )

  return tooltip ? (
    <SimpleTooltip content={tooltip}>{content}</SimpleTooltip>
  ) : (
    <>{content}</>
  )
}

interface ToolbarGroupProps extends HTMLAttributes<HTMLDivElement> {
  noSeparator?: boolean
  groupClassName?: string
}

const ToolbarGroup: FC<ToolbarGroupProps> = ({
  noSeparator,
  className,
  children,
  groupClassName
}) => {
  if (!children) return null

  return (
    <div className={cn('flex', className)}>
      {!noSeparator ? (
        <div className="h-full py-1">
          <Separator orientation="vertical" />
        </div>
      ) : null}
      <div className={cn('mx-1 mr-0 flex items-center gap-1', groupClassName)}>
        {children}
      </div>
    </div>
  )
}

export { Toolbar, ToolbarButton, ToolbarGroup }
